import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taskStatusesControllerCreateStatusV1,
  taskStatusesControllerDeleteStatusV1,
  taskStatusesControllerListStatusesV1,
  taskStatusesControllerReorderStatusesV1,
  taskStatusesControllerUpdateStatusV1,
} from '@/api/generated/task-statuses/task-statuses';
import {
  teamsControllerCreateTeamV1,
  teamsControllerDeleteTeamV1,
  teamsControllerListTeamsV1,
  teamsControllerUpdateTeamV1,
} from '@/api/generated/teams/teams';
import {
  taskLabelsControllerCreateLabelV1,
  taskLabelsControllerDeleteLabelV1,
  taskLabelsControllerListLabelsV1,
  taskLabelsControllerUpdateLabelV1,
} from '@/api/generated/task-labels/task-labels';
import { usersControllerListMembersV1 } from '@/api/generated/users/users';
import type {
  CreateTaskLabelDto,
  CreateTaskStatusDto,
  CreateTeamDto,
  TaskStatusDto,
  UpdateTaskLabelDto,
  UpdateTaskStatusDto,
  UpdateTeamDto,
} from '@/api/generated/model';
import { taskKeys } from './useTasks';

/**
 * The agency-level collections tasks refer to: workflow statuses, teams and
 * labels. Read by the board on every visit and cached for a while — they
 * change in Settings, far less often than tasks do — and written only from
 * Settings (owner-only on the server).
 */
const SETTINGS_STALE = 5 * 60_000;

export function useTaskStatusesQuery() {
  return useQuery({
    queryKey: taskKeys.statuses,
    queryFn: ({ signal }) => taskStatusesControllerListStatusesV1({ signal }),
    staleTime: SETTINGS_STALE,
  });
}

export function useTeamsQuery() {
  return useQuery({
    queryKey: taskKeys.teams,
    queryFn: ({ signal }) => teamsControllerListTeamsV1({ signal }),
    staleTime: SETTINGS_STALE,
  });
}

export function useTaskLabelsQuery() {
  return useQuery({
    queryKey: taskKeys.labels,
    queryFn: ({ signal }) => taskLabelsControllerListLabelsV1({ signal }),
    staleTime: SETTINGS_STALE,
  });
}

/**
 * Members for assignee pickers and mentions. The API caps a page at 100,
 * which covers any agency this product is sized for; see PRODUCTION.md.
 */
export function useAssignableMembersQuery() {
  return useQuery({
    queryKey: taskKeys.members,
    queryFn: ({ signal }) => usersControllerListMembersV1({ limit: 100 }, { signal }),
    select: (page) => page.data,
    staleTime: SETTINGS_STALE,
  });
}

/** A settings write changes how every task renders, so boards refetch too. */
function useInvalidate(key: readonly string[]) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: key }),
      queryClient.invalidateQueries({ queryKey: taskKeys.all }),
    ]);
}

// ─── Statuses ─────────────────────────────────────────────────────────────────

export function useCreateTaskStatusMutation() {
  const invalidate = useInvalidate(taskKeys.statuses);
  return useMutation({
    mutationFn: (dto: CreateTaskStatusDto) => taskStatusesControllerCreateStatusV1(dto),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTaskStatusMutation() {
  const invalidate = useInvalidate(taskKeys.statuses);
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTaskStatusDto }) =>
      taskStatusesControllerUpdateStatusV1(id, dto),
    onSuccess: () => invalidate(),
  });
}

/** Reorders optimistically — a dragged column should not jump back while saving. */
export function useReorderTaskStatusesMutation() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate(taskKeys.statuses);
  return useMutation({
    mutationFn: (ids: string[]) => taskStatusesControllerReorderStatusesV1({ ids }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.statuses });
      const previous = queryClient.getQueryData<TaskStatusDto[]>(taskKeys.statuses);
      if (previous) {
        const byId = new Map(previous.map((status) => [status.id, status]));
        queryClient.setQueryData(
          taskKeys.statuses,
          ids.map((id) => byId.get(id)).filter((s): s is TaskStatusDto => Boolean(s)),
        );
      }
      return { previous };
    },
    onError: (_error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(taskKeys.statuses, context.previous);
    },
    onSettled: () => invalidate(),
  });
}

export function useDeleteTaskStatusMutation() {
  const invalidate = useInvalidate(taskKeys.statuses);
  return useMutation({
    mutationFn: ({ id, moveTo }: { id: string; moveTo?: string }) =>
      taskStatusesControllerDeleteStatusV1(id, moveTo ? { moveTo } : undefined),
    onSuccess: () => invalidate(),
  });
}

// ─── Teams ────────────────────────────────────────────────────────────────────

export function useCreateTeamMutation() {
  const invalidate = useInvalidate(taskKeys.teams);
  return useMutation({
    mutationFn: (dto: CreateTeamDto) => teamsControllerCreateTeamV1(dto),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTeamMutation() {
  const invalidate = useInvalidate(taskKeys.teams);
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTeamDto }) =>
      teamsControllerUpdateTeamV1(id, dto),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteTeamMutation() {
  const invalidate = useInvalidate(taskKeys.teams);
  return useMutation({
    mutationFn: (id: string) => teamsControllerDeleteTeamV1(id),
    onSuccess: () => invalidate(),
  });
}

// ─── Labels ───────────────────────────────────────────────────────────────────

export function useCreateTaskLabelMutation() {
  const invalidate = useInvalidate(taskKeys.labels);
  return useMutation({
    mutationFn: (dto: CreateTaskLabelDto) => taskLabelsControllerCreateLabelV1(dto),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTaskLabelMutation() {
  const invalidate = useInvalidate(taskKeys.labels);
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTaskLabelDto }) =>
      taskLabelsControllerUpdateLabelV1(id, dto),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteTaskLabelMutation() {
  const invalidate = useInvalidate(taskKeys.labels);
  return useMutation({
    mutationFn: (id: string) => taskLabelsControllerDeleteLabelV1(id),
    onSuccess: () => invalidate(),
  });
}
